/** Supplier rate-deck manager: paginated price list with search,
 *  add/edit/delete single rates, CSV import + export.
 *  Backed by /routing/suppliers/{id}/rates endpoints.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import apiClient from "../../api/client";
import { useNotify } from "../common/NotificationProvider";
import IncrementField from "../common/IncrementField";
import { effPPM } from "../../utils/rating";

interface Props {
  open: boolean;
  supplierId: string;
  supplierName: string;
  onClose: () => void;
}

interface RateEntry { prefix: string; rate: number; connect_fee: number; billing_increment: number }

const PAGE_SIZE = 100;

export default function SupplierRatesDialog({ open, supplierId, supplierName, onClose }: Props) {
  const notify = useNotify();
  const [rates, setRates] = useState<RateEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [newPrefix, setNewPrefix] = useState("");
  const [newRate, setNewRate] = useState("");
  const [newFee, setNewFee] = useState("0");
  const [newInc, setNewInc] = useState("1");
  const [editing, setEditing] = useState<{ prefix: string; rate: string; fee: string; inc: string } | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const load = useCallback(async (opts?: { page?: number; search?: string }) => {
    if (!supplierId) return;
    setLoading(true);
    setError("");
    try {
      const r = await apiClient.get(`/api/v1/routing/suppliers/${encodeURIComponent(supplierId)}/rates`, {
        params: {
          page: (opts?.page ?? page) + 1,
          page_size: PAGE_SIZE,
          search: opts?.search ?? search,
        },
      });
      setRates(r.data.rates || []);
      setTotal(r.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load rates");
    } finally {
      setLoading(false);
    }
  }, [supplierId, page, search]);

  useEffect(() => { if (open) { setPage(0); setSearch(""); load({ page: 0, search: "" }); } }, [open, supplierId]); // eslint-disable-line
  useEffect(() => { if (open) load({ page }); }, [page]); // eslint-disable-line

  const saveOne = async (prefix: string, rate: number, fee: number, inc: number) => {
    await apiClient.put(
      `/api/v1/routing/suppliers/${encodeURIComponent(supplierId)}/rates/${encodeURIComponent(prefix)}`,
      { rate, connect_fee: fee, billing_increment: inc },
    );
    notify(`Rate saved: ${prefix}`);
    load({ page });
  };

  const handleDelete = async (prefix: string) => {
    try {
      await apiClient.delete(
        `/api/v1/routing/suppliers/${encodeURIComponent(supplierId)}/rates/${encodeURIComponent(prefix)}`,
      );
      notify(`Rate deleted: ${prefix}`);
      load({ page });
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to delete", "error");
    }
  };

  const handleImport = async (file: File) => {
    const text = await file.text();
    const rows = text.split(/\r?\n/).filter(Boolean).slice(1).map((line) => {
      const [prefix, rate, connect_fee, billing_increment] = line.split(",");
      const inc = parseInt(billing_increment ?? "", 10);
      return {
        prefix: prefix?.trim(),
        rate: parseFloat(rate),
        connect_fee: parseFloat(connect_fee) || 0,
        // canonical range 1–3600 (rate_boundary.parse_increment server-side)
        billing_increment: inc >= 1 && inc <= 3600 ? inc : 1,
      };
    }).filter((r) => r.prefix && !isNaN(r.rate));
    if (!rows.length) { notify("No valid rows found in CSV", "error"); return; }
    setBusy(true);
    try {
      const res = await apiClient.post(
        `/api/v1/routing/suppliers/${encodeURIComponent(supplierId)}/rates/import`,
        { rows },
      );
      notify(`Import: ${res.data.created} created, ${res.data.updated} updated, ${res.data.failed} failed`);
      load({ page: 0 });
    } catch (err: any) {
      notify(err.response?.data?.detail || "Import failed", "error");
    } finally {
      setBusy(false);
    }
  };
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    const res = await apiClient.get(
      `/api/v1/routing/suppliers/${encodeURIComponent(supplierId)}/rates/export`,
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${supplierId}_rates.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Rate Deck — {supplierName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small" label="Search prefix" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load({ page: 0, search: searchInput })}
            sx={{ width: 200 }}
          />
          <Button size="small" onClick={() => load({ page: 0, search: searchInput })}>Search</Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<DownloadIcon />} onClick={handleExport}>Export CSV</Button>
          <Button
            size="small" startIcon={<CloudUploadIcon />}
            onClick={() => fileRef.current?.click()} disabled={busy}
          >
            Import CSV
          </Button>
          <input
            ref={fileRef} type="file" accept=".csv" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImport(f); if (e.target) e.target.value = ""; }}
          />
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => {
            setEditing(null); setNewPrefix(""); setNewRate(""); setNewFee("0"); setNewInc("1"); setAddOpen(true);
          }}>
            Add Rate
          </Button>
          {/* Add / Edit rate dialog */}
          <Dialog open={addOpen} onClose={() => !busy && setAddOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>{editing?.prefix ? `Edit Rate — ${editing.prefix}` : "Add Rate"}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                {!editing?.prefix && (
                  <TextField
                    label="Prefix" required fullWidth value={newPrefix}
                    onChange={(e) => setNewPrefix(e.target.value)}
                    placeholder="e.g. 88017" autoFocus
                  />
                )}
                <TextField
                  label="Rate ($/min)" required fullWidth type="number"
                  inputProps={{ step: 0.000001, min: 0.000001 }}
                  value={newRate} onChange={(e) => setNewRate(e.target.value)}
                  helperText="Per minute — e.g. 0.00723; up to 6 decimals"
                />
                <TextField
                  label="Connect Fee ($)" fullWidth type="number"
                  inputProps={{ step: 0.0001, min: 0 }}
                  value={newFee} onChange={(e) => setNewFee(e.target.value)}
                />
                <IncrementField value={newInc} onChange={setNewInc} />
                <Typography variant="caption" color="text.secondary">
                  Eff. PPM @60s incl. connect fee:{" "}
                  {effPPM(parseFloat(newRate) || 0, parseFloat(newFee) || 0, parseInt(newInc, 10) || 1).toFixed(6)}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddOpen(false)} disabled={busy}>Cancel</Button>
              <Button
                variant="contained" disabled={busy || !newPrefix.trim() || !(parseFloat(newRate) > 0)}
                onClick={async () => {
                  await saveOne(newPrefix.trim(), parseFloat(newRate), parseFloat(newFee) || 0, parseInt(newInc, 10) || 1);
                  setAddOpen(false); setNewPrefix(""); setNewRate(""); setNewFee("0"); setNewInc("1");
                }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Prefix</TableCell>
                <TableCell align="right">Rate $/min</TableCell>
                <TableCell align="right">Connect fee</TableCell>
                <TableCell align="right">Increment</TableCell>
                <TableCell align="right">Eff. PPM @60s</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rates.map((r) => (
                <TableRow key={r.prefix}>
                  <TableCell sx={{ fontFamily: "monospace" }}>{r.prefix}</TableCell>
                  <TableCell align="right">{Number(r.rate).toFixed(6)}</TableCell>
                  <TableCell align="right">{Number(r.connect_fee).toFixed(6)}</TableCell>
                  <TableCell align="right">{r.billing_increment || 1}s</TableCell>
                  <TableCell align="right" title="Price of a 60s call incl. connect fee, per minute">
                    {effPPM(Number(r.rate), Number(r.connect_fee), r.billing_increment || 1).toFixed(6)}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => {
                      setEditing({ prefix: r.prefix, rate: String(r.rate), fee: String(r.connect_fee), inc: String(r.billing_increment || 1) });
                      setNewPrefix(r.prefix); setNewRate(String(r.rate)); setNewFee(String(r.connect_fee)); setNewInc(String(r.billing_increment || 1));
                      setAddOpen(true);
                    }}
                      sx={{ mr: 1 }}>Edit</Button>
                    <Button size="small" color="error" onClick={() => handleDelete(r.prefix)}>Del</Button>
                  </TableCell>
                </TableRow>
              ))}
              {rates.length === 0 && (
                <TableRow><TableCell colSpan={6}>No rates — import a CSV or add one.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={PAGE_SIZE}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={() => {}}
          rowsPerPageOptions={[PAGE_SIZE]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
        />
        {editing && (
          <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{editing.prefix}</Typography>
            <TextField
              size="small" label="New rate" type="number" value={editing.rate}
              onChange={(e) => setEditing({ ...editing, rate: e.target.value })} sx={{ width: 140 }}
            />
            <TextField
              size="small" label="Connect fee" type="number" value={editing.fee}
              onChange={(e) => setEditing({ ...editing, fee: e.target.value })} sx={{ width: 120 }}
            />
            <IncrementField
              label="Increment" value={editing.inc}
              onChange={(v) => setEditing({ ...editing, inc: v })}
            />
            <Button
              variant="contained" size="small"
              onClick={async () => {
                const rate = parseFloat(editing.rate);
                if (!(rate > 0)) return;
                await saveOne(editing.prefix, rate, parseFloat(editing.fee) || 0, parseInt(editing.inc, 10) || 1);
                setEditing(null);
              }}
            >
              Save
            </Button>
            <Button size="small" onClick={() => setEditing(null)}>Cancel</Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
